import "maplibre-gl/dist/maplibre-gl.css"

import { useEffect, useState, useRef } from "react"
import { useRouter } from "next/router"
import { useIntersectionObserver } from "usehooks-ts"

export default function Map({
  // default center is Roosevelt Arizona
  lng: initialLng = -111.134153997,
  lat: initialLat = 33.6672587976,
  initialZoom = 6,
  locations = [],
  scrollToZoom = true,
  flyTo,
}) {
  const router = useRouter()

  const mapContainer = useRef(null)
  const map = useRef(null)

  const [lng] = useState(initialLng)
  const [lat] = useState(initialLat)
  const [markers, setMarkers] = useState([])

  const currentLocation = locations.find(({ name }) => name === flyTo)

  const entry = useIntersectionObserver(mapContainer, {})
  const isVisible = !!entry?.isIntersecting

  // initialize map (warning: fair amount of imperative code below)
  useEffect(() => {
    if (map.current) return

    async function loadMap() {
      const maplibregl = (await import("maplibre-gl")).default
      const layers = (await import("components/mapLayersStyles")).default

      map.current = new maplibregl.Map({
        container: mapContainer.current,
        center: [lng, lat],
        minPitch: 0,
        minZoom: 6,
        maxZoom: 15,
        pitch: 0,
        bearing: 10,
        style: {
          version: 8,
          glyphs: "/gis/glyphs/{fontstack}/{range}.pbf",
          sources: {
            protomaps: {
              type: "vector",
              tiles: ["https://maps.2060.services/basemapsz15/{z}/{x}/{y}.mvt"],
              attribution: `The Husband & Wife Law Team © <a href="https://openstreetmap.org">OpenStreetMap Contributors</a>`,
            },
          },
          layers,
        },
      })

      // markers/locations & popups
      const markers = locations.map(({ lat, lng, name, address, slug }) => {
        const mapPin = document.createElement("div")
        mapPin.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" fill="none" class="map-pin"><path fill="#10A2FC" d="M15 0A15.03 15.03 0 0 0 0 15.02 15.2 15.2 0 0 0 4.5 25.9a35.6 35.6 0 0 1 8.82 14.27l.1.25c.5 1.6 2.72 1.6 3.21 0l.05-.15a34.04 34.04 0 0 1 8.72-14.22A15.2 15.2 0 0 0 30 15.17 15.1 15.1 0 0 0 15 0Zm0 23.75a8.6 8.6 0 0 1-8.56-8.63A8.6 8.6 0 0 1 15 6.49a8.63 8.63 0 0 1 0 17.26Z"/></svg>'

        const popupBody = document.createElement("div")
        popupBody.innerHTML = `
        <h5 class="location-title text-m1 lg:text-m2 pb-2 lg:pb-4">${name}</h5>
        <address class="text-s2 not-italic">${address}</address>
        <button class="bg-purple absolute bottom-0 right-0 p-4"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="8" fill="none"><path fill="white" d="M15.27 4.35a.5.5 0 0 0 0-.7L12.09.46a.5.5 0 1 0-.71.71L14.2 4l-2.83 2.83a.5.5 0 1 0 .7.7l3.19-3.18ZM.42 4.5h14.5v-1H.41v1Z"/></svg></button>`

        popupBody.querySelector("button").addEventListener("click", () => router.push(slug))

        const popup = new maplibregl.Popup({
          offset: {
            bottom: [0, -34],
          },
          closeOnMove: true,
          closeButton: false,
          focusAfterOpen: false,
          maxWidth: "280px",
          anchor: "bottom",
        }).setDOMContent(popupBody)

        const marker = new maplibregl.Marker({
          element: mapPin,
          anchor: "bottom",
        })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(map.current)

        mapPin.addEventListener("click", () => {
          map.current.flyTo({ center: [lng, lat + 0.0012], zoom: 15, speed: 1.7 })
          map.current.once("moveend", () => marker.togglePopup())
        })

        return marker
      })
      // toggled after location traversal below
      setMarkers(markers)

      // controls
      map.current.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-left")

      map.current.on("load", () => {
        map.current.flyTo({ center: [lng, lat], pitch: 45, zoom: initialZoom, bearing: 0, duration: 1000 })
        if (mapContainer.current) {
          mapContainer.current.classList.add("loaded")
        }
      })

      if (!scrollToZoom) {
        map.current.scrollZoom.disable()
      }
    }

    if (isVisible) {
      loadMap()
    }
  }, [lat, isVisible, lng, initialZoom, locations, flyTo, router, scrollToZoom])

  useEffect(() => {
    if (currentLocation?.coords) {
      const marker = markers.find((marker) => {
        const markerCoords = Object.values(marker.getLngLat()).toString()
        const currentCoords = currentLocation.coords.map((v) => +v).toString()

        return markerCoords === currentCoords
      })

      map.current.flyTo({ center: currentLocation.coords, zoom: 15, speed: 1.5 })
      map.current.once("moveend", () => marker?.togglePopup())
    }
  }, [currentLocation, markers, flyTo])

  return <div ref={mapContainer} className="absolute w-full h-full inset-0 object-cover" />
}
